import ArrowLeftIcon from "@/assets/icons/ArrowLeft";
import Screen from "@/components/layout/Screen";
import { ActiveSessionCard } from "@/components/shared/ActiveSessionCard";
import BaseModal from "@/components/shared/BaseModal";
import PageTitle from "@/components/shared/PageTitle/PageTitle";
import PassphraseModal from "@/components/shared/PassphraseModal";
import RateSelection from "@/components/shared/RateSelection";
import { Box } from "@/components/ui/box";
import { Heading } from "@/components/ui/heading";
import { HStack } from "@/components/ui/hstack";
import { Skeleton, SkeletonText } from "@/components/ui/skeleton";
import { VStack } from "@/components/ui/vstack";
import { useRates } from "@/hooks/useRates";
import { useSessionsData } from "@/hooks/useSessions";
import { useToastNotify } from "@/stores/toastStore";
import { saveToStorage } from "@/utils/secureStore";
import * as Sentry from "@sentry/react-native";
import { t } from "@transifex/native";
import { useFocusEffect, useRouter } from "expo-router";
import { useLocalSearchParams } from "expo-router/build/hooks";
import type { RateMini } from "ipctg-inflow-types/InFlowAPIMisc";
import { useState } from "react";
import { Platform } from "react-native";
import { v4 as uuidv4 } from "uuid";

const SelectRate = () => {
  const { sessionID = "" } = useLocalSearchParams<{ sessionID?: string }>();
  const { canExtend = "false" } = useLocalSearchParams<{ canExtend?: string }>();
  const { data: sessionData } = useSessionsData();
  const { rates, isFetching, noRatesMessage } = useRates();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { notify } = useToastNotify();
  const router = useRouter();

  const [selectedRate, setSelectedRate] = useState<RateMini | null>(null);
  const [isPassphraseModalOpen, setIsPassphraseModalOpen] = useState(false);

  const session = sessionData?.parkingSessions?.find((s) => s.session?.sessionID === sessionID)?.session ?? null;

  const safeRates = rates ?? [];

  useFocusEffect(() => {
    const initializeState = async () => {
      try {
        await saveToStorage("isPurchasingPass", "false");
      } catch (error) {
        console.error("Error initializing state:", error);
        Sentry.captureException(error, {
          extra: {
            localStorage: Platform.OS === "web" ? window.localStorage : null,
          },
        });
      }
    };

    initializeState();
  });

  const handleContinue = (rate: RateMini) => {
    const requiresPlate = session && canExtend === "true";
    if (requiresPlate && !session?.plate) {
      notify({ severity: "warning", message: t("Plate is required for this session.") });
      return;
    }

    setSelectedRate(rate);

    if (rate.requiresPassphrase) {
      setIsPassphraseModalOpen(true);
    } else if (rate.warningMessage) {
      setIsModalOpen(true);
    } else {
      router.push({
        params: { isPurchasingPass: "false", rateID: rate.rateID, rateName: rate.rateName },
        pathname: "/cart",
      });
    }
  };

  const navigateToCart = () => {
    router.push({
      params: { isPurchasingPass: "false", rateID: selectedRate?.rateID, rateName: selectedRate?.rateName },
      pathname: "/cart",
    });
    setSelectedRate(null);
  };

  if (isFetching) {
    return <SelectRateSkeleton />;
  }

  return (
    <Screen>
      <VStack className="gap-[32px]">
        <PageTitle title={t("Parking Rates")} />

        {session && <ActiveSessionCard session={session} canExtend={false} onExtend={() => {}} />}
        <Box>
          {safeRates.length > 0 ? (
            <RateSelection rates={safeRates} onRateSelected={handleContinue} />
          ) : (
            <Box className="text-center">
              <Heading className="text-center text-typography-900 text-lg">{noRatesMessage}</Heading>
            </Box>
          )}
        </Box>

        <PassphraseModal
          isOpen={isPassphraseModalOpen}
          onClose={() => setIsPassphraseModalOpen(false)}
          rateID={selectedRate?.rateID || ""}
          rateName={selectedRate?.rateName || ""}
          onSuccess={() => {
            router.push({
              params: { isPurchasingPass: "false", rateID: selectedRate?.rateID, rateName: selectedRate?.rateName },
              pathname: "/cart",
            });
          }}
        />

        <BaseModal
          title={t("Notice")}
          subtitle={selectedRate?.warningMessage || ""}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          buttons={[
            { title: "Back", onPress: () => setIsModalOpen(false), variant: "outline", icon: ArrowLeftIcon },
            {
              title: "Proceed",
              onPress: () => {
                setIsModalOpen(false);
                const selectedRateDetails = rates?.find((rate) => rate.rateID === selectedRate?.rateID);
                if (selectedRateDetails?.requiresPassphrase) {
                  setIsPassphraseModalOpen(true);
                } else {
                  navigateToCart();
                }
              },
            },
          ]}
        />
      </VStack>
    </Screen>
  );
};

const SelectRateSkeleton = () => {
  return (
    <Screen>
      <VStack space="md" className="gap-[32px]">
        <Box className="items-center">
          <SkeletonText className="h-8 w-3/4" />
        </Box>
        <VStack space="sm" className="gap-[12px]">
          {[...Array(5)].map(() => (
            <Skeleton key={uuidv4()} className="h-[64px] w-full rounded-md" />
          ))}
        </VStack>
        <HStack space="md" className="mt-4">
          <Skeleton className="h-12 w-1/2 rounded-md" />
          <Skeleton className="h-12 w-1/2 rounded-md" />
        </HStack>
      </VStack>
    </Screen>
  );
};

export default SelectRate;
